import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Copy, Check, Download, ExternalLink, ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL, downloadLaunchPlanPdf } from "../lib/api";
import { generateDetailedBuilderPrompt } from "../lib/prompt";
import { BUILDERS } from "../data/builders";
import { BuilderLogo } from "./BuilderLogo";
import { usePro } from "../hooks/usePro";

export const BuildModal = ({ business, onClose }) => {
  const { isPro, attempt } = usePro();
  const [builder, setBuilder] = useState(null);
  const [copied, setCopied] = useState(false);
  const [launchBusy, setLaunchBusy] = useState(false);

  const blueprint = useMemo(() => generateDetailedBuilderPrompt(business, builder), [business, builder]);

  useEffect(() => {
    setBuilder(null);
    setCopied(false);
  }, [business]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copyBlueprint = async () => {
    try {
      await navigator.clipboard.writeText(blueprint);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = blueprint;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Builder Prompt copied");
    setTimeout(() => setCopied(false), 2500);
  };

  const downloadLaunchPlan = async () => {
    if (launchBusy) return;
    if (!isPro) { attempt("launch_plan"); return; }
    setLaunchBusy(true);
    try {
      // Build a blueprint-like payload from the business idea for the AI Launch Plan.
      const bp = {
        project_name: business.title,
        elevator_pitch: business.tagline || business.summary || "",
        solution: business.description || business.long_description || business.summary || "",
        target_audience: business.audience || business.target_audience || "",
        revenue_model: business.monetization || business.revenue_model || "",
        category: business.category || "",
        builder_prompt: blueprint,
      };
      await downloadLaunchPlanPdf(bp);
      toast.success("Launch Plan downloaded");
    } catch { toast.error("Could not generate Launch Plan. Please try again."); }
    finally { setLaunchBusy(false); }
  };

  const openBuilder = () => window.open(builder.url, "_blank", "noopener,noreferrer");

  return (
    <AnimatePresence>
      {business && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F0F0F]/55 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="build-modal-overlay"
        >
          <motion.div
            className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl relative overflow-hidden flex flex-col max-h-[92vh]"
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="build-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-5 right-5 text-white/70 hover:text-white transition-colors z-10"
              data-testid="build-modal-close-x"
            >
              <X size={22} />
            </button>

            {/* dark header */}
            <div className="bg-[#0F0F0F] px-8 pt-7 pb-6">
              <div className="flex items-center gap-2 mb-3">
                <img src={LOGO_URL} alt="LaunchBS" className="h-8 w-8 object-contain" />
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#D4AF37]">
                  <Sparkles size={15} /> AI Builder Hub
                </span>
              </div>
              {!builder ? (
                <>
                  <h2 className="font-display text-2xl font-bold text-white">Choose Your AI Builder</h2>
                  <p className="text-sm text-[#A1A1AA] mt-1">
                    Select a platform to build <span className="text-white font-medium">{business.title}</span> — we'll generate a detailed, production-ready Build Prompt optimized for it.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="font-display text-2xl font-bold text-white">Build Prompt</h2>
                  <p className="text-sm text-[#A1A1AA] mt-1">
                    <span className="text-white font-medium">{business.title}</span> · optimized for {builder.name}
                  </p>
                </>
              )}
            </div>

            {/* body */}
            {!builder ? (
              <div className="px-8 py-6 overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {BUILDERS.map((b) => (
                    <div
                      key={b.id}
                      className={`rounded-2xl border p-4 flex flex-col ${
                        b.recommended ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-gray-100"
                      }`}
                      data-testid={`builder-option-${b.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <BuilderLogo url={b.url} name={b.name} size={28} />
                        <h3 className="font-display font-semibold text-[#0F0F0F]">{b.name}</h3>
                        {b.recommended && (
                          <span className="text-[10px] font-semibold bg-[#D4AF37] text-[#0F0F0F] px-2 py-0.5 rounded-full">
                            Recommended
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#687280] leading-relaxed flex-1">{b.tagline}</p>
                      <button
                        onClick={() => setBuilder(b)}
                        className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-colors ${
                          b.recommended
                            ? "bg-[#D4AF37] text-[#0F0F0F] hover:bg-[#c5a030]"
                            : "bg-[#0F0F0F] text-white hover:bg-[#1c1c1c]"
                        }`}
                        data-testid={`build-with-${b.id}-btn`}
                      >
                        {b.cta}
                      </button>
                    </div>
                  ))}
                </div>

                {/* Which builder table */}
                <div className="mt-8">
                  <h4 className="font-display font-semibold text-[#0F0F0F] mb-3">
                    Which Builder Should I Choose?
                  </h4>
                  <div className="rounded-2xl border border-gray-100 overflow-hidden">
                    {BUILDERS.map((b, i) => (
                      <div
                        key={b.id}
                        className={`flex items-center justify-between px-4 py-3 text-sm ${
                          i % 2 ? "bg-[#F7F7F7]" : "bg-white"
                        }`}
                      >
                        <span className="flex items-center gap-2 font-medium text-[#0F0F0F]"><BuilderLogo url={b.url} name={b.name} size={22} />{b.name}</span>
                        <span className="text-[#687280]">{b.bestFor}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="px-8 py-6 flex-1 overflow-hidden flex flex-col">
                <button
                  onClick={() => setBuilder(null)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#687280] hover:text-[#0F0F0F] transition-colors mb-4 self-start"
                  data-testid="change-builder-btn"
                >
                  <ArrowLeft size={15} /> Choose a different builder
                </button>
                <textarea
                  readOnly
                  value={blueprint}
                  onFocus={(e) => e.target.select()}
                  className="w-full flex-1 min-h-[260px] bg-[#F7F7F7] border border-gray-100 rounded-2xl p-5 text-xs text-[#0F0F0F] leading-relaxed font-mono resize-none outline-none focus:ring-2 focus:ring-[#D4AF37] whitespace-pre-wrap"
                  data-testid="blueprint-textarea"
                />
                <div className="flex flex-col sm:flex-row gap-3 pt-5">
                  <button
                    onClick={copyBlueprint}
                    className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold transition-colors ${
                      copied ? "bg-green-500 text-white" : "bg-[#0F0F0F] text-white hover:bg-[#1c1c1c]"
                    }`}
                    data-testid="copy-blueprint-btn"
                  >
                    {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy Builder Prompt</>}
                  </button>
                  <button
                    onClick={downloadLaunchPlan}
                    disabled={launchBusy}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold border border-gray-200 text-[#0F0F0F] hover:bg-[#F7F7F7] transition-colors disabled:opacity-60"
                    data-testid="download-launch-plan-btn"
                  >
                    {launchBusy ? <><Loader2 size={16} className="animate-spin" /> Generating…</> : <><Download size={16} /> 📥 Download Launch Plan</>}
                  </button>
                  <button
                    onClick={openBuilder}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold bg-[#D4AF37] text-[#0F0F0F] hover:bg-[#c5a030] transition-colors"
                    data-testid="open-builder-btn"
                  >
                    Open {builder.name} <ExternalLink size={15} />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
